import type { WidgetDataRequirements } from "@nexus/widget-sdk";
import type { CSSProperties } from "react";
import {
  createDateFormatter,
  displayDate,
  formatDisplayDay,
  useDisplayTimezone,
  type TimezoneMode,
} from "@nexus/ui";
import {
  firstError,
  MetricValue,
  unwrap,
  Widget,
  WidgetSkeleton,
  WidgetState,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetProps } from "@nexus/widget-sdk";
import {
  motionClass,
  PIRACY_AUDIENCE_SERIES_CONTRACT,
  useAnimatedNumber,
  useWidgetMotion,
  type BlockingPeriod,
  type PiracyAudienceSeriesData,
} from "@nexus/widget-kit";
import "./styles.css";

export interface BlockingPeriodSpotlightConfig {
  title?: string;
  /**
   * Read by the dashboard, which owns playback: `false` keeps the latest period
   * on show instead of cycling through them. Default `true`.
   */
  autoplay?: boolean;
  /** Seconds each period stays on show while cycling (5, 10, 15 or 30). Default 5. */
  intervalSeconds?: number;
  showProgress?: boolean;
  animateValue?: boolean;
}

export const manifest: WidgetManifest = {
  id: "blocking-period-spotlight",
  name: "Blocking periods",
  description: "A blocking period and its modeled audience disruption, selected by the dashboard.",
  version: "1.1.0",
  dataRequests: {
    series: {
      source: "piracy-audience",
      contract: PIRACY_AUDIENCE_SERIES_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: BlockingPeriodSpotlightConfig = { title: manifest.name };

export function getDataRequirements(): Record<string, WidgetDataRequirements> {
  return {
    series: {
      mode: "select",
      fields: [
        "blockingPeriods.id",
        "blockingPeriods.from",
        "blockingPeriods.to",
        "blockingPeriods.status",
        "blockingPeriods.viewersDisrupted",
        "blockingApplies",
        "freshness",
      ],
    },
  };
}

function validPeriods(periods: readonly BlockingPeriod[]) {
  return [...periods]
    .filter(
      (period) =>
        Number.isFinite(Date.parse(period.from)) && Date.parse(period.to) > Date.parse(period.from),
    )
    .sort((left, right) => Date.parse(left.from) - Date.parse(right.from));
}

export function selectSpotlightPeriod(
  periods: readonly BlockingPeriod[],
  activeId?: string | null,
) {
  const ordered = validPeriods(periods);
  const index = activeId === undefined ? 0 : ordered.findIndex((period) => period.id === activeId);
  const period = ordered[index];
  return period ? { period, index, total: ordered.length, ordered } : null;
}

const hasImpact = (
  period: BlockingPeriod,
): period is BlockingPeriod & { viewersDisrupted: number } =>
  typeof period.viewersDisrupted === "number" && Number.isFinite(period.viewersDisrupted);

export interface ImpactRank {
  /** 1 for the period that disrupted the most viewers; ties share a rank. */
  rank: number;
  /** Periods with a known impact, the population of the ranking. */
  ranked: number;
  /** This period's impact relative to the largest one, 0–1. */
  share: number;
}

/**
 * Where a period stands among the periods shown, by viewers disrupted. `null`
 * when its impact is unknown or fewer than two periods have one: a ranking of
 * one, or against unknown values, says nothing.
 */
export function rankPeriodImpact(
  periods: readonly BlockingPeriod[],
  period: BlockingPeriod,
): ImpactRank | null {
  if (!hasImpact(period)) return null;
  const known = periods.filter(hasImpact).map((item) => item.viewersDisrupted);
  if (known.length < 2) return null;
  const max = Math.max(...known);
  return {
    rank: 1 + known.filter((value) => value > period.viewersDisrupted).length,
    ranked: known.length,
    share: max > 0 ? period.viewersDisrupted / max : 0,
  };
}

export function formatImpactRank({ rank, ranked }: ImpactRank): string {
  return rank === 1 ? "Highest impact" : `#${rank} of ${ranked} by impact`;
}

export function formatBlockingDuration(period: Pick<BlockingPeriod, "from" | "to">): string {
  const milliseconds = Date.parse(period.to) - Date.parse(period.from);
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return "Unavailable";
  const minutes = Math.ceil(milliseconds / 60_000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const remainder = minutes % 60;
  return [days ? `${days} d` : "", hours ? `${hours} h` : "", remainder ? `${remainder} min` : ""]
    .filter(Boolean)
    .join(" ");
}

const count = new Intl.NumberFormat("en", { maximumFractionDigits: 0 });
export function formatBlockingPeriodRange(
  period: Pick<BlockingPeriod, "from" | "to">,
  timezone: TimezoneMode = "utc",
): string {
  const date = createDateFormatter(timezone, { day: "numeric", month: "short" }, "en");
  const time = createDateFormatter(
    timezone,
    { hour: "2-digit", minute: "2-digit", hourCycle: "h23" },
    "en",
  );
  const from = new Date(period.from);
  const to = new Date(period.to);
  if (!Number.isFinite(from.getTime()) || !Number.isFinite(to.getTime()) || to <= from)
    return "Unavailable";
  const start = `${date.format(from)}, ${time.format(from)}`;
  const end =
    displayDate(from, timezone).toISOString().slice(0, 10) ===
    displayDate(to, timezone).toISOString().slice(0, 10)
      ? time.format(to)
      : `${date.format(to)}, ${time.format(to)}`;
  return `${start} - ${end}`;
}

/**
 * The period's identity as two distance-readable parts: the day it started
 * ("Sat 16 Aug") and its hours ("18:00–20:45", or "22:00 → Sun 01:30"
 * across midnight). `null` for an invalid period.
 */
export function formatPeriodIdentity(
  period: Pick<BlockingPeriod, "from" | "to">,
  timezone: TimezoneMode = "utc",
): { day: string; hours: string } | null {
  const weekday = createDateFormatter(timezone, { weekday: "short" }, "en");
  const time = createDateFormatter(
    timezone,
    { hour: "2-digit", minute: "2-digit", hourCycle: "h23" },
    "en",
  );
  const from = new Date(period.from);
  const to = new Date(period.to);
  if (!Number.isFinite(from.getTime()) || !Number.isFinite(to.getTime()) || to <= from) return null;
  const sameDay =
    displayDate(from, timezone).toISOString().slice(0, 10) ===
    displayDate(to, timezone).toISOString().slice(0, 10);
  return {
    day: formatDisplayDay(from, timezone),
    hours: sameDay
      ? `${time.format(from)}–${time.format(to)}`
      : `${time.format(from)} → ${weekday.format(to)} ${time.format(to)}`,
  };
}

export function Component({
  data,
  config,
  presentation,
}: WidgetProps<BlockingPeriodSpotlightConfig>) {
  const timezone = useDisplayTimezone();
  const error = firstError(data, ["series"]);
  const result = unwrap<PiracyAudienceSeriesData>(data.series);
  const loading = data.series?.isLoading && !result;
  const selection = presentation?.blockingPeriodSelection;
  const chosen = result?.blockingPeriods
    ? selectSpotlightPeriod(result.blockingPeriods, selection?.activePeriodId)
    : null;
  const period = chosen?.period;
  const estimate = period?.viewersDisrupted;
  const noBlocking = result?.blockingApplies === false;
  const available = !noBlocking && typeof estimate === "number" && Number.isFinite(estimate);
  const motion = useWidgetMotion();
  const shown = useAnimatedNumber(available ? estimate : null, {
    enabled: motion && config.animateValue === true,
  });
  const rank = chosen && period && !noBlocking ? rankPeriodImpact(chosen.ordered, period) : null;
  const identity = period ? formatPeriodIdentity(period, timezone) : null;
  const cadence = selection?.cadenceMs;
  const state =
    result?.blockingPeriods === undefined ? "Blocking periods unavailable" : "No blocking periods";

  return (
    <Widget title={config.title?.trim() || manifest.name} intrinsicHeight>
      <div className="density-metric-padding flex min-h-32 min-w-0 flex-1 flex-col gap-3">
        {loading ? (
          <WidgetSkeleton shape="kpi" />
        ) : period && chosen ? (
          <div className="flex min-w-0 flex-1 flex-col gap-3" data-motion={motion || undefined}>
            <div
              key={`identity-${period.id}`}
              className={`${motionClass(motion, "nx-rise")} flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-metric-caption`}
            >
              <span className="font-semibold text-primary">
                {identity?.day ?? formatBlockingPeriodRange(period, timezone)}
              </span>
              {identity ? (
                <span className="tabular-nums text-secondary">{identity.hours}</span>
              ) : null}
              {period.status === "active" ? (
                <span className="bps-live inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-metric-detail font-semibold text-primary">
                  <span aria-hidden className="bps-live-dot" />
                  Active
                </span>
              ) : null}
            </div>
            <div>
              <div className="flex min-w-0 flex-wrap items-end justify-between gap-x-3 gap-y-2">
                <MetricValue size="headline" title={available ? count.format(estimate) : undefined}>
                  {shown !== null ? count.format(Math.round(shown)) : "—"}
                </MetricValue>
                {rank ? (
                  <span
                    key={`rank-${period.id}`}
                    className={`${motionClass(motion, "nx-pop")} shrink-0 rounded-control bg-surface-elevated px-2 py-1 text-metric-detail font-semibold text-primary tabular-nums`}
                    data-rank={rank.rank}
                    style={{ "--nx-delay": "220ms" } as CSSProperties}
                  >
                    {formatImpactRank(rank)}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-metric-label text-secondary">
                {available
                  ? "Estimated viewers disrupted"
                  : noBlocking
                    ? "No blocking"
                    : "Estimate unavailable"}
              </p>
            </div>
            {chosen.total > 1 ? (
              <div className="mt-auto flex flex-col gap-1.5">
                {config.showProgress !== false && cadence ? (
                  <div aria-hidden className="relative h-[3px] overflow-hidden">
                    <span
                      key={`progress-${period.id}`}
                      className="nx-progress bps-progress absolute inset-0"
                      data-inactive={noBlocking || undefined}
                      style={{ "--nx-duration": `${cadence}ms` } as CSSProperties}
                    />
                  </div>
                ) : null}
                <p className="text-metric-detail text-secondary tabular-nums">
                  Period {chosen.index + 1} of {chosen.total}
                </p>
              </div>
            ) : (
              <p className="mt-auto text-metric-detail text-secondary">
                Period {chosen.index + 1} of {chosen.total}
              </p>
            )}
          </div>
        ) : error ? (
          <WidgetIssueState error={error} />
        ) : (
          <WidgetState title={state} />
        )}
      </div>
    </Widget>
  );
}
